import React, { useState, useRef, useEffect } from 'react';
import CFQRecommendations from './CFQRecommendations';
import { ArrowRight, BookOpen, ExternalLink, Download, Mail, X, Check, Linkedin, Twitter, RotateCcw, Shield, Users, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import { domains } from './cfqData';
import { getInterpretation, getDomainInsight, getBandLabel, getNextStep } from './cfqScoring';
import { PILLAR_ICON_URLS } from '../../components/PillarIcons';

const DOMAIN_ICONS = [
  PILLAR_ICON_URLS.selfAnchoring,
  PILLAR_ICON_URLS.perspectiveShifting,
  PILLAR_ICON_URLS.adaptiveCommunication,
  PILLAR_ICON_URLS.relationalIntegration,
];

const DOMAIN_ALT = [
  'Self-Anchoring icon — anchor symbol representing grounded identity and internal coherence',
  'Perspective-Shifting icon — magnifying glass representing cultural insight and perspective-taking',
  'Adaptive Communication icon — dialogue symbol representing communication across difference',
  'Relational Integration icon — interlocking puzzle symbol representing shared space and connection',
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TCL_ICON_URL   = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/c4h0snj7_Third%20culture%20Leadership%20icon.png';
const TCL_BANNER_URL = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/jxiucnvi_Third%20culture%20leadership%20banner.webp';

const BAND_ORDER = ['Developing', 'Emerging', 'Established', 'Advanced'];

/** Load image as base64 data URL WITH natural dimensions (for PDF embedding). */
const loadImageWithDims = (url) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth || 600; c.height = img.naturalHeight || 150;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve({ data: c.toDataURL('image/png'), w: img.naturalWidth || 600, h: img.naturalHeight || 150 });
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = url;
});

/** Load any image URL as a base64 data URL (for PDF embedding). */
const loadImageAsDataURL = (url) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth || 200; c.height = img.naturalHeight || 200;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve(c.toDataURL('image/png'));
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = url;
});

const CFQResultsPanel = ({
  userDetails, domainScores, totalScore, sessionCode, isTestMode, onRestart, previousAttempt,
}) => {
  const interpretation = getInterpretation(totalScore);
  const resultsRef = useRef(null);

  const [iconDataUrl, setIconDataUrl] = useState(null);
  const [bannerInfo,  setBannerInfo]  = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ recipientEmail: '' });
  const [emailSent, setEmailSent] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [historyCleared, setHistoryCleared] = useState(false);

  const [pulseSent, setPulseSent] = useState(false);
  const [mailingEmail, setMailingEmail] = useState('');
  const [mailingName, setMailingName] = useState('');
  const [mailingNotifySessions, setMailingNotifySessions] = useState(false);
  const [mailingSubmitted, setMailingSubmitted] = useState(false);

  const [sessionInfo, setSessionInfo] = useState(null);
  const [shareDisplayName, setShareDisplayName] = useState('');
  const [shareSent, setShareSent] = useState(false);

  // Preload TCL icon and banner for PDF embedding
  useEffect(() => {
    loadImageAsDataURL(TCL_ICON_URL).then(setIconDataUrl);
    loadImageWithDims(TCL_BANNER_URL).then(setBannerInfo);
  }, []);

  // Scroll to the top instantly when results mount — avoids the jarring
  // "backwards scroll" from the bottom of the assessment form.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    if (!sessionCode) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/sessions/public/session-status/${sessionCode}`);
        if (res.ok) setSessionInfo(await res.json());
      } catch (e) { console.error('Session status fetch failed:', e); }
    })();
  }, [sessionCode]);

  const handleShareWithFacilitator = async () => {
    if (!shareDisplayName.trim()) return;
    try {
      await fetch(`${API_URL}/api/sessions/public/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_code: sessionCode,
          display_name: shareDisplayName,
          self_anchoring_band: getBandLabel(domainScores[0]),
          perspective_shifting_band: getBandLabel(domainScores[1]),
          adaptive_communication_band: getBandLabel(domainScores[2]),
          relational_integration_band: getBandLabel(domainScores[3]),
          share_full_answers: false,
        }),
      });
      setShareSent(true);
    } catch (e) { console.error('Share with facilitator failed:', e); }
  };

  const handlePulseSubmit = async () => {
    try {
      await fetch(`${API_URL}/api/cfq/pulse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          self_anchoring_band: getBandLabel(domainScores[0]),
          perspective_shifting_band: getBandLabel(domainScores[1]),
          adaptive_communication_band: getBandLabel(domainScores[2]),
          relational_integration_band: getBandLabel(domainScores[3]),
          session_code: sessionCode || undefined,
          source: sessionCode ? 'qr' : 'site',
        }),
      });
      setPulseSent(true);
    } catch (e) { console.error('Pulse submit failed:', e); }
  };

  const handleMailingSubmit = async (e) => {
    e.preventDefault();
    if (!mailingEmail) return;
    try {
      // 1. Subscribe to mailing list (existing flow)
      await fetch(`${API_URL}/api/cfq/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: mailingEmail, first_name: mailingName, notify_sessions: mailingNotifySessions }),
      });

      // 2. Register for the 24-hour personalised follow-up email
      const overallBand =
        totalScore >= 80 ? 'Advanced' :
        totalScore >= 60 ? 'Established' :
        totalScore >= 40 ? 'Emerging' : 'Developing';

      await fetch(`${API_URL}/api/cfq/register-followup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: mailingEmail,
          first_name: mailingName,
          overall_band: overallBand,
          self_anchoring_band: getBandLabel(domainScores[0]),
          perspective_shifting_band: getBandLabel(domainScores[1]),
          adaptive_communication_band: getBandLabel(domainScores[2]),
          relational_integration_band: getBandLabel(domainScores[3]),
        }),
      });

      setMailingSubmitted(true);
    } catch (e) { console.error('Mailing submit failed:', e); }
  };

  // ── PDF helpers ──
  const pdfHeaderWithIcon = (pdf, title, subtitle) => {
    const pw = pdf.internal.pageSize.getWidth();
    pdf.setFillColor(7, 27, 58);
    pdf.rect(0, 0, pw, 52, 'F');
    // Icon — top right of header bar
    if (iconDataUrl) {
      try { pdf.addImage(iconDataUrl, 'PNG', pw - 34, 8, 26, 26); } catch (e) { console.warn('PDF icon render skipped:', e); }
    }
    // Label
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(201, 147, 22);
    pdf.text(subtitle, 20, 20);
    // Title
    pdf.setFontSize(20); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255);
    pdf.text(title, 20, 36);
  };

  const generateExecutiveSummaryPDF = () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const addText = (text, fontSize, fontStyle = 'normal', color = [10, 22, 40], lineHeight = 1.4) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', fontStyle);
      pdf.setTextColor(color[0], color[1], color[2]);
      const lines = pdf.splitTextToSize(text, contentWidth);
      lines.forEach((line) => {
        if (y > pageHeight - margin - 10) { pdf.addPage(); y = margin; }
        pdf.text(line, margin, y);
        y += fontSize * 0.35 * lineHeight;
      });
    };
    const addSpace = (s) => { y += s; if (y > pageHeight - margin) { pdf.addPage(); y = margin; } };
    const addDivider = () => { pdf.setDrawColor(220, 210, 190); pdf.line(margin, y, pageWidth - margin, y); y += 6; };

    pdfHeaderWithIcon(pdf, 'Executive Summary', 'CULTURAL FLUENCY QUOTIENT (CFQ)');
    y = 68;

    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(100, 100, 100);
    pdf.text(`Prepared for: ${userDetails.name || '[Name]'}`, margin, y); y += 5;
    if (userDetails.role || userDetails.organisation) {
      pdf.text(`Role / Organisation: ${userDetails.role || ''} ${userDetails.organisation ? '· ' + userDetails.organisation : ''}`, margin, y); y += 5;
    }
    pdf.text(`Date: ${new Date(userDetails.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, y); y += 5;
    pdf.text('Framework: Third Culture Leadership · Author: Promise Monday · Promond Publishing', margin, y);
    addSpace(15); addDivider();

    addText('What This Report Represents', 12, 'bold'); addSpace(4);
    addText('This executive summary provides a high-level reflection of your Cultural Fluency Quotient — a diagnostic lens that examines how you lead, communicate, and relate across cultural difference.', 9);
    addSpace(10); addDivider();

    addText('Your CFQ Profile', 12, 'bold'); addSpace(4);
    addText(interpretation.insight, 10, 'italic'); addSpace(6);
    pdf.setFontSize(8); pdf.setTextColor(150, 150, 150);
    pdf.text(`Overall: ${totalScore}/100`, margin, y); y += 5;
    domains.forEach((d, i) => { pdf.text(`${d.name}: ${domainScores[i]}/25 (${getBandLabel(domainScores[i])})`, margin, y); y += 4; });
    addSpace(10); addDivider();

    domains.forEach((d, i) => {
      if (y > pageHeight - 60) { pdf.addPage(); y = margin; }
      addText(`Domain ${i + 1}: ${d.name}`, 11, 'bold');
      addText(d.subtitle, 9, 'italic', [150, 150, 150]); addSpace(3);
      addText(`Band: ${getBandLabel(domainScores[i])} (${domainScores[i]}/25)`, 9, 'bold', [80, 80, 80]);
      addText(`Profile: ${getDomainInsight(i, domainScores[i])}`, 9, 'normal', [80, 80, 80]); addSpace(2);
      addText(`Next step: ${getNextStep(i, getBandLabel(domainScores[i]))}`, 9, 'italic', [100, 80, 40]); addSpace(8);
    });

    pdf.setFillColor(7, 27, 58);
    pdf.rect(0, pageHeight - 20, pageWidth, 20, 'F');
    pdf.setFontSize(8); pdf.setTextColor(255, 255, 255);
    pdf.text('Third Culture Leadership · thirdcultureleadership.com · Promond Publishing', margin, pageHeight - 8);
    if (iconDataUrl) {
      try { pdf.addImage(iconDataUrl, 'PNG', pageWidth - 22, pageHeight - 18, 14, 14); } catch (e) { console.warn('PDF icon render skipped:', e); }
    }

    pdf.save(`CFQ-Executive-Summary-${userDetails.name || 'Report'}-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const generateReflectionGuidePDF = () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const checkPage = (n = 20) => { if (y > pageHeight - margin - n) { pdf.addPage(); y = margin; } };
    const addText = (text, fs, style = 'normal', color = [10, 22, 40], lh = 1.5) => {
      pdf.setFontSize(fs); pdf.setFont('helvetica', style); pdf.setTextColor(color[0], color[1], color[2]);
      pdf.splitTextToSize(text, contentWidth).forEach((line) => { checkPage(10); pdf.text(line, margin, y); y += fs * 0.35 * lh; });
    };
    const addSpace = (s) => { y += s; checkPage(); };
    const addLines = (count = 3) => { for (let i = 0; i < count; i++) { checkPage(8); pdf.setDrawColor(220, 210, 190); pdf.line(margin, y, pageWidth - margin, y); y += 8; } };
    const addQ = (q) => { checkPage(30); addText(q, 9, 'normal', [40, 40, 40]); addSpace(3); addLines(2); addSpace(4); };

    pdfHeaderWithIcon(pdf, 'Reflection Worksheet', 'CFQ POST-ASSESSMENT');
    // Overlay banner in header (centred)
    if (bannerInfo) {
      try {
        const pw = pdf.internal.pageSize.getWidth();
        const bh = 18; const bw = bh * (bannerInfo.w / bannerInfo.h);
        pdf.addImage(bannerInfo.data, 'PNG', pw / 2 - bw / 2, 10, bw, bh);
      } catch (e) { console.warn('PDF banner embed skipped:', e); }
    }
    y = 72;

    addText('This worksheet supports leadership clarity, not self-judgement.', 9, 'italic', [100, 100, 100]); addSpace(8);

    domains.forEach((d, i) => {
      checkPage(25); addSpace(6);
      pdf.setFillColor(7, 27, 58); pdf.rect(margin - 2, y - 5, contentWidth + 4, 12, 'F');
      pdf.setFontSize(10); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255);
      pdf.text(`${d.name} — ${getBandLabel(domainScores[i])} (${domainScores[i]}/25)`, margin + 2, y + 2);
      y += 14;
      addText(`Next step: ${getNextStep(i, getBandLabel(domainScores[i]))}`, 9, 'italic', [100, 80, 40]); addSpace(4);
      addQ('What does this domain reveal about my leadership patterns?');
      addQ('Where am I strongest here — and where am I inconsistent?');
    });

    const pages = pdf.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      pdf.setPage(p);
      pdf.setFillColor(7, 27, 58); pdf.rect(0, pageHeight - 12, pageWidth, 12, 'F');
      pdf.setFontSize(7); pdf.setTextColor(255, 255, 255);
      pdf.text('CFQ Reflection Worksheet · Third Culture Leadership · Promond Publishing', margin, pageHeight - 5);
      pdf.setTextColor(201, 147, 22); pdf.text(`${p} / ${pages}`, pageWidth - margin - 8, pageHeight - 5);
      if (iconDataUrl && p === 1) {
        try { pdf.addImage(iconDataUrl, 'PNG', pageWidth - 22, pageHeight - 11, 9, 9); } catch (e) { console.warn('PDF icon render skipped:', e); }
      }
    }

    pdf.save(`CFQ-Reflection-Worksheet-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try { generateExecutiveSummaryPDF(); } catch (e) { console.error(e); }
    setIsExporting(false);
  };

  const exportToImage = async () => {
    if (!resultsRef.current) return;
    setIsExporting(true);
    try {
      const canvas = await html2canvas(resultsRef.current, { scale: 2, useCORS: true, backgroundColor: '#FAF8F3' });
      const link = document.createElement('a');
      link.download = `CFQ-Results-${userDetails.name || 'Assessment'}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) { console.error(e); }
    setIsExporting(false);
  };

  const shareOnLinkedIn = () => {
    const url = `${window.location.origin}/cfq-assessment`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=600');
  };
  const shareOnTwitter = () => {
    const text = 'I completed the Cultural Fluency Quotient — a reflective tool for leading across cultures.';
    const url = `${window.location.origin}/cfq-assessment`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}&hashtags=CulturalFluency,Leadership`, '_blank', 'width=600,height=400');
  };
  const handleShareViaEmail = () => {
    const subject = encodeURIComponent('My CFQ Results - Cultural Fluency Quotient');
    const body = encodeURIComponent(
      `Cultural Fluency Quotient Results\n\nOverall: ${totalScore}/100\n${domains.map((d, i) => `${d.name}: ${domainScores[i]}/25 (${getBandLabel(domainScores[i])})`).join('\n')}\n\nTake yours: ${window.location.origin}/cfq-assessment`
    );
    window.location.href = `mailto:${emailData.recipientEmail}?subject=${subject}&body=${body}`;
    setEmailSent(true);
    setTimeout(() => { setShowEmailModal(false); setEmailSent(false); setEmailData({ recipientEmail: '' }); }, 2000);
  };

  const bandColors = {
    'Advanced':    { bg: 'bg-emerald-50', text: 'text-emerald-700' },
    'Established': { bg: 'bg-blue-50',    text: 'text-blue-700'    },
    'Emerging':    { bg: 'bg-amber-50',   text: 'text-amber-700'   },
    'Developing':  { bg: 'bg-slate-100',  text: 'text-slate-600'   },
  };

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="cfq-results-panel">

      {/* Test Mode Banner */}
      {isTestMode && (
        <div className="bg-amber-500 px-5 py-3 flex items-center justify-center gap-2" data-testid="test-mode-banner">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-amber-900"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
          <span className="text-amber-900 text-sm font-medium">Test Mode — Your results will <strong>not</strong> appear in the facilitator's room</span>
        </div>
      )}

      {/* ── Results Header — kept dark navy for dramatic score reveal ── */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#071B3A' }}>
        <div className="absolute top-0 right-0 w-[40%] h-full bg-gradient-to-l from-[#C99316]/[0.03] to-transparent pointer-events-none" />
        <div className="relative z-10 py-20 px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Brand icon in header */}
            <div className="flex justify-center mb-8">
              <div
                className="flex items-center justify-center"
                style={{ width: '64px', height: '64px', borderRadius: '12px', backgroundColor: 'rgba(250,248,243,0.07)', border: '1px solid rgba(250,248,243,0.06)' }}
              >
                <img src={TCL_ICON_URL} alt="Third Culture Leadership" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
              </div>
            </div>

            <div className="flex items-center gap-3 mb-8">
              <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(201,147,22,0.2)' }} />
              <p className="text-[11px] uppercase tracking-[0.3em] font-medium" style={{ color: 'rgba(201,147,22,0.7)' }}>
                Your Cultural Fluency Profile
              </p>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(201,147,22,0.2)' }} />
            </div>
            <p className="text-xl md:text-3xl leading-relaxed font-light text-center" style={{ color: 'rgba(255,255,255,0.95)' }} data-testid="results-insight">
              {interpretation.insight}
            </p>

            {sessionCode && (
              <div className="mt-6 bg-white/5 border border-white/10 rounded-lg px-5 py-3 text-center">
                <p className="text-white/50 text-sm">This is private. The facilitator will not see your score.</p>
              </div>
            )}

            {userDetails.name && (
              <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-center gap-6 text-white/40 text-sm flex-wrap">
                <span>{userDetails.name}</span>
                {userDetails.organisation && <><span className="w-1 h-1 rounded-full bg-[#C99316]/50" /><span>{userDetails.organisation}</span></>}
                <span className="w-1 h-1 rounded-full bg-[#C99316]/50" />
                <span>{new Date(userDetails.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
            )}
          </div>
        </div>
        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(201,147,22,0.2) 30%, rgba(201,147,22,0.2) 70%, transparent)' }} />
      </div>

      {/* ── Actions Bar — stays dark as continuation of header ── */}
      <div style={{ backgroundColor: '#071B3A', borderTop: '1px solid rgba(255,255,255,0.05)' }} className="py-4 px-6">
        <div className="max-w-3xl mx-auto flex flex-wrap gap-2 justify-center">
          {[
            { onClick: exportToPDF,              icon: Download,  label: 'Save PDF',         disabled: isExporting },
            { onClick: generateReflectionGuidePDF, icon: BookOpen, label: 'Reflection Guide'  },
            { onClick: exportToImage,            icon: Download,  label: 'Save Image',        disabled: isExporting },
            { onClick: () => setShowEmailModal(true), icon: Mail,  label: 'Share'             },
          ].map((btn) => (
            <button
              key={btn.label} onClick={btn.onClick} disabled={btn.disabled}
              data-testid={`action-btn-${btn.label.toLowerCase().replace(/\s/g, '-')}`}
              className="flex items-center gap-2 text-white/50 px-4 py-2.5 text-sm rounded-full border border-white/10 hover:text-white/80 hover:border-white/20 hover:bg-white/5 transition-all duration-300 disabled:opacity-50"
            >
              <btn.icon size={14} />{btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Share Modal ── */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-md w-full rounded-xl shadow-2xl overflow-hidden">
            <div className="px-6 py-4" style={{ backgroundColor: '#071B3A' }}>
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-white">Share Your Profile</h3>
                <button onClick={() => setShowEmailModal(false)} className="text-white/60 hover:text-white"><X size={18} /></button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-3">
                <button onClick={shareOnLinkedIn} data-testid="share-linkedin-btn" className="flex-1 flex items-center justify-center gap-2 bg-[#0077B5] text-white py-3 rounded-lg text-sm font-medium hover:bg-[#0077B5]/90 transition-colors"><Linkedin size={16} /> LinkedIn</button>
                <button onClick={shareOnTwitter} data-testid="share-twitter-btn" className="flex-1 flex items-center justify-center gap-2 bg-black text-white py-3 rounded-lg text-sm font-medium hover:bg-black/90 transition-colors"><Twitter size={16} /> X</button>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-navy mb-3">Share via Email</p>
                <input type="email" placeholder="Recipient email" value={emailData.recipientEmail} onChange={(e) => setEmailData({ recipientEmail: e.target.value })} className="w-full border border-navy/10 rounded-lg px-4 py-3 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-gold/20" data-testid="share-email-input" />
                {emailSent ? (
                  <div className="flex items-center gap-2 text-green-600 text-sm"><Check size={16} /> Email client opened!</div>
                ) : (
                  <button onClick={handleShareViaEmail} disabled={!emailData.recipientEmail} data-testid="share-email-send-btn" className="w-full text-white py-3 rounded-lg text-sm font-medium disabled:opacity-50 transition-all" style={{ backgroundColor: '#071B3A' }}>Send Email</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Results Content (ivory) ── */}
      <div ref={resultsRef}>

        {/* Overall Score */}
        <div className="py-12 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white border border-navy/[0.07] rounded-xl p-8 md:p-10 text-center shadow-sm shadow-navy/[0.03]">
              <p className="text-xs uppercase tracking-[0.2em] text-navy/35 font-medium mb-4">Overall Score</p>
              <div className="font-light text-[#071B3A]" style={{ fontSize: '4rem', lineHeight: 1 }} data-testid="total-score">
                {totalScore}<span className="text-2xl text-navy/25">/100</span>
              </div>
              <p className="text-base font-semibold mt-3" style={{ color: '#C99316' }}>{getBandLabel(totalScore, 100)}</p>
              <p className="text-navy/55 mt-4 max-w-md mx-auto text-[17px] leading-relaxed">{interpretation.reflection}</p>
            </div>
          </div>
        </div>

        {/* Growth Comparison — shown when previous attempt exists */}
        {previousAttempt && !historyCleared && (
          <div className="px-6 pb-6">
            <div className="max-w-3xl mx-auto">
              <div
                className="bg-white border border-gold/[0.2] rounded-xl p-8 shadow-sm shadow-navy/[0.02]"
                data-testid="growth-comparison-panel"
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center flex-shrink-0">
                      <TrendingUp size={16} className="text-gold" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-navy">Growth Since Last Attempt</p>
                      <p className="text-navy/35 text-xs mt-0.5">
                        {'Compared to ' + new Date(previousAttempt.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  {/* Score delta badge */}
                  <span
                    className={'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold flex-shrink-0 ' + (
                      totalScore - previousAttempt.totalScore > 0
                        ? 'bg-emerald-50 text-emerald-700'
                        : totalScore - previousAttempt.totalScore < 0
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-slate-100 text-slate-500'
                    )}
                    data-testid="score-delta-badge"
                  >
                    {totalScore - previousAttempt.totalScore > 0 ? '+' : ''}
                    {totalScore - previousAttempt.totalScore} pts
                  </span>
                </div>

                {/* Per-domain band comparison rows */}
                <div className="space-y-0">
                  {domains.map((domain, i) => {
                    const prevBand = getBandLabel(previousAttempt.domainScores[i]);
                    const currBand = getBandLabel(domainScores[i]);
                    const prevRank = BAND_ORDER.indexOf(prevBand);
                    const currRank = BAND_ORDER.indexOf(currBand);
                    const improved = currRank > prevRank;
                    const declined = currRank < prevRank;
                    return (
                      <div
                        key={i}
                        className="flex items-center gap-3 py-3 border-b border-navy/[0.04] last:border-0"
                        data-testid={'comparison-domain-' + i}
                      >
                        <p className="text-sm text-navy/55 flex-1 min-w-0 truncate">{domain.name}</p>
                        <div className="flex items-center gap-2 text-xs flex-shrink-0">
                          <span className="text-navy/30">{prevBand}</span>
                          <ArrowRight size={11} className="text-navy/20" />
                          <span className={improved ? 'text-emerald-600 font-semibold' : declined ? 'text-amber-600 font-semibold' : 'text-navy/50 font-medium'}>
                            {currBand}
                          </span>
                          {improved && <TrendingUp size={11} className="text-emerald-500" />}
                          {declined && <span className="text-amber-500 text-[9px] font-bold">&#9660;</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Subtle "Clear history" option */}
              <div className="flex justify-end mt-3">
                <button
                  onClick={() => {
                    try { localStorage.removeItem('cfq_history'); } catch {}
                    setHistoryCleared(true);
                  }}
                  className="text-[10px] uppercase tracking-[0.18em] text-navy/25 hover:text-navy/45 transition-colors duration-200 font-sans"
                  data-testid="clear-cfq-history-btn"
                >
                  Clear history
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Domain Insights */}
        <div className="py-6 px-6">          <div className="max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-[0.2em] text-navy/35 font-medium mb-6">Domain Insights</p>
            <div className="space-y-4">
              {domains.map((domain, index) => {
                const band = getBandLabel(domainScores[index]);
                const bc = bandColors[band] || bandColors['Developing'];
                return (
                  <div key={index} className="bg-white border border-navy/[0.07] rounded-xl p-6 md:p-8 hover:shadow-sm hover:border-gold/20 transition-all shadow-sm shadow-navy/[0.02]" data-testid={`domain-result-${index}`}>
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-start gap-4">
                        <img
                          src={DOMAIN_ICONS[index]}
                          alt={DOMAIN_ALT[index]}
                          width={48}
                          height={48}
                          style={{ objectFit: 'contain', display: 'block', flexShrink: 0 }}
                          draggable={false}
                        />
                        <div>
                          <h3 className="font-semibold text-xl text-navy">{domain.name}</h3>
                          <p className="text-sm mt-1 font-medium" style={{ color: '#C99316' }}>{domain.subtitle}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-light text-navy">{domainScores[index]}</span>
                        <span className="text-sm text-navy/30">/25</span>
                        <span className={`block mt-1 text-xs font-medium px-2.5 py-0.5 rounded-full ${bc.bg} ${bc.text}`}>{band}</span>
                      </div>
                    </div>
                    <div className="mb-5">
                      <div className="h-1.5 bg-navy/[0.06] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: ((domainScores[index] / 25) * 100) + '%', backgroundColor: '#C99316' }} />
                      </div>
                      <div className="flex justify-between mt-1.5">
                        {['Developing', 'Emerging', 'Established', 'Advanced'].map((b) => (
                          <span key={b} className="text-[11px]" style={{ color: band === b ? '#C99316' : 'rgba(7,27,58,0.2)', fontWeight: band === b ? 600 : 400 }}>{b}</span>
                        ))}
                      </div>
                    </div>
                    <p className="text-navy/60 text-base leading-relaxed mb-4">{getDomainInsight(index, domainScores[index])}</p>
                    <div className="bg-gold/[0.06] border border-gold/[0.15] rounded-lg p-4">
                      <p className="text-xs font-medium uppercase tracking-wide mb-1.5" style={{ color: '#C99316' }}>Your next step</p>
                      <p className="text-navy/65 text-[16px] leading-relaxed">{getNextStep(index, band)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Anonymous Pulse */}
      <div className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white border border-navy/[0.07] rounded-xl p-8 md:p-10 shadow-sm shadow-navy/[0.02]">
            <div className="flex items-start gap-4 mb-4">
              <Shield size={20} className="text-gold mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg text-navy mb-2">Help improve the CFQ (optional)</h3>
                <p className="text-navy/50 text-[16px] leading-relaxed mb-1">Share your four pillar bands anonymously to help us understand patterns across sessions.</p>
                <p className="text-navy/30 text-[13px] leading-relaxed">We store only: {sessionCode ? `session code "${sessionCode}", ` : ''}today's date, and four band labels. No name, email, or individual answers.</p>
              </div>
            </div>
            {pulseSent ? (
              <div className="flex items-center gap-2 text-emerald-600 text-sm mt-4" data-testid="pulse-sent-msg"><Check size={16} /> Thank you. Your anonymous data has been recorded.</div>
            ) : (
              <div className="mt-4 flex gap-3">
                <button onClick={handlePulseSubmit} className="px-5 py-3 text-white text-[16px] rounded-lg hover:opacity-90 transition-opacity" style={{ backgroundColor: '#071B3A' }} data-testid="pulse-opt-in-btn">Share anonymously</button>
                <button onClick={() => setPulseSent(true)} className="px-4 py-3 text-navy/40 text-[16px] hover:text-navy/60" data-testid="pulse-no-thanks-btn">No thanks</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Test mode: show note instead of facilitator share */}
      {sessionCode && isTestMode && (
        <div className="py-6 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-3" data-testid="test-mode-share-note">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-600 mt-0.5 flex-shrink-0"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
              <div>
                <p className="text-amber-800 text-sm font-medium mb-0.5">Test Mode Active</p>
                <p className="text-amber-700 text-sm leading-relaxed">This is a practice run. Your results are visible to you only — nothing has been shared with the facilitator room or added to the session count. Remove <code className="bg-amber-100 px-1 py-0.5 rounded text-xs">&amp;test=1</code> from the URL to run the real assessment.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share with Facilitator — hidden in test mode */}
      {sessionCode && !isTestMode && sessionInfo?.testable && (
        <div className="py-6 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white border border-blue-100 rounded-xl p-8 md:p-10 shadow-sm shadow-navy/[0.02]">
              <div className="flex items-start gap-4 mb-4">
                <Users size={20} className="text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg text-navy mb-2">Share with your facilitator (optional)</h3>
                  <p className="text-navy/50 text-[16px] leading-relaxed">
                    The facilitator of session <span className="font-medium text-navy">{sessionCode}</span> will see only the display name you choose and your four pillar bands.
                  </p>
                </div>
              </div>
              {shareSent ? (
                <div className="flex items-center gap-2 text-emerald-600 text-sm mt-4" data-testid="share-facilitator-sent"><Check size={16} /> Shared. The facilitator can now see your band summary.</div>
              ) : (
                <div className="mt-4 space-y-3">
                  <input type="text" value={shareDisplayName} onChange={e => setShareDisplayName(e.target.value)} placeholder="Display name (not your legal name)" maxLength={50} className="w-full bg-cream-dark border border-navy/10 rounded-lg px-4 py-3 text-[16px] text-navy placeholder-navy/30 focus:outline-none focus:ring-2 focus:ring-blue-200" data-testid="share-display-name-input" />
                  <div className="flex gap-3">
                    <button onClick={handleShareWithFacilitator} disabled={!shareDisplayName.trim()} className="px-5 py-3 bg-blue-600 text-white text-[16px] rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid="share-facilitator-btn">Share my summary</button>
                    <button onClick={() => setShareSent(true)} className="px-4 py-3 text-navy/40 text-[16px] hover:text-navy/60">No thanks</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mailing List */}
      <div className="py-6 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white border border-navy/[0.07] rounded-xl p-8 md:p-10 shadow-sm shadow-navy/[0.02]">
            <h3 className="font-semibold text-lg text-navy mb-2">Stay in touch (optional)</h3>
            <p className="text-navy/50 text-[16px] leading-relaxed mb-4">Receive occasional articles on cultural fluency. Your email is never linked to your results.</p>
            {mailingSubmitted ? (
              <div className="flex items-center gap-2 text-emerald-600 text-sm" data-testid="mailing-sent-msg"><Check size={16} /> You're on the list. Check your inbox to confirm.</div>
            ) : (
              <form onSubmit={handleMailingSubmit} className="space-y-3" data-testid="mailing-form">
                <div className="flex flex-wrap gap-3">
                  <input type="text" value={mailingName} onChange={(e) => setMailingName(e.target.value)} placeholder="First name" className="bg-cream-dark border border-navy/10 rounded-lg px-4 py-3 text-[16px] text-navy placeholder-navy/30 focus:outline-none focus:ring-2 focus:ring-gold/20 w-36" data-testid="mailing-name-input" />
                  <input type="email" value={mailingEmail} onChange={(e) => setMailingEmail(e.target.value)} required placeholder="Email" className="bg-cream-dark border border-navy/10 rounded-lg px-4 py-3 text-[16px] text-navy placeholder-navy/30 focus:outline-none focus:ring-2 focus:ring-gold/20 flex-1 min-w-[200px]" data-testid="mailing-email-input" />
                  <button type="submit" className="px-5 py-3 text-white text-[16px] rounded-lg hover:opacity-90" style={{ backgroundColor: '#C99316' }} data-testid="mailing-submit-btn">Subscribe</button>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={mailingNotifySessions} onChange={(e) => setMailingNotifySessions(e.target.checked)} className="rounded border-navy/20 text-gold focus:ring-gold/20" data-testid="mailing-notify-checkbox" />
                  <span className="text-navy/50 text-sm">Tell me if Dr Monday runs a session with partners.</span>
                </label>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ── Recommended For You ── */}
      <div style={{ backgroundColor: '#0B1628', borderTop: '1px solid rgba(245,240,232,0.05)' }}>
        <CFQRecommendations domainScores={domainScores} />
      </div>

      {/* ── Continue Your Journey ── */}
      <div className="py-10 px-6" style={{ backgroundColor: '#071B3A' }}>
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-3">
              <Link to="/books" className="flex items-center gap-2 text-white/60 px-5 py-2.5 text-sm rounded-full border border-white/10 hover:border-white/20 hover:bg-white/5 transition-all"><BookOpen size={14} /> Explore the Book</Link>
              <a href="https://www.linkedin.com/in/promisemonday" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/60 px-5 py-2.5 text-sm rounded-full border border-white/10 hover:border-white/20 hover:bg-white/5 transition-all"><ExternalLink size={14} /> Connect on LinkedIn</a>
            </div>
            <button onClick={() => setShowRestartConfirm(true)} className="flex items-center gap-2 text-white/30 text-xs hover:text-white/50 transition-colors" data-testid="restart-btn">
              <RotateCcw size={12} /> Start Over
            </button>
          </div>
        </div>
      </div>

      {/* Restart Confirmation */}
      {showRestartConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full text-center shadow-2xl border border-navy/[0.08]">
            <h3 className="font-semibold text-navy mb-2">Start Over?</h3>
            <p className="text-navy/50 text-sm mb-6">This will clear your current responses.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={onRestart} className="px-6 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600" data-testid="confirm-restart-btn">Yes, restart</button>
              <button onClick={() => setShowRestartConfirm(false)} className="px-6 py-2 border border-navy/15 text-navy/50 rounded-lg text-sm hover:bg-navy/[0.03]" data-testid="cancel-restart-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CFQResultsPanel;
